import { useEffect, useState, useMemo } from 'react';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import FlightsListView from './FlightsListView';
import FlightsAsideFilters from 'legacyfrontend/components/flights/AsideFilters/FlightsAsideFilter';
import MobileFlightsFilter from 'legacyfrontend/components/flights/AsideFilters/MobileAsideFilters';
import { Stack } from '@mui/material';
import { FlightsFiltersProvider } from 'legacyfrontend/contexts/FlightsFiltersContext';
import { FlightProvider } from 'legacyfrontend/contexts/FlightDetailsContext';
import SearchSelector from '../../../components/home/components/SearchSelector';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { useLocation } from 'react-router-dom';
import { Seo } from 'core/components/seo';
import { useAppName } from 'legacyfrontend/hooks/useSafeState';

const FLIGHT_RESULTS_FILTERS_MOBILE_BREAKPOINT = 1024;

const buildSeoFromParams = (search, appName) => {
  if (!search) {
    return {
      title: appName,
      description: `Encontrá los mejores vuelos en ${appName}. Compará precios, aerolíneas y horarios. Reservá online.`,
    };
  }

  const params = new URLSearchParams(search);
  const originName = params.get('originName');
  const destinationName = params.get('destinationName');
  const departureDate = params.get('departureDate');
  const returnDate = params.get('returnDate');

  if (originName && destinationName && departureDate) {
    const depDate = new Date(departureDate + 'T00:00:00');
    const monthNames = [
      '', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
    ];
    const depDay = depDate.getDate();
    const depMonth = monthNames[depDate.getMonth() + 1] || depDate.getMonth() + 1;

    const title = appName;
    let description = `Vuelos desde ${originName} a ${destinationName} para el ${depDay} de ${depMonth}.`;

    if (returnDate) {
      const retDate = new Date(returnDate + 'T00:00:00');
      const retDay = retDate.getDate();
      const retMonth = monthNames[retDate.getMonth() + 1] || retDate.getMonth() + 1;
      description = `Vuelos ida y vuelta desde ${originName} a ${destinationName}. Ida: ${depDay} de ${depMonth}. Vuelta: ${retDay} de ${retMonth}. Los mejores precios en ${appName}.`;
    } else {
      description += ` Los mejores precios en ${appName}. Reservá online.`;
    }

    return { title, description };
  }

  if (originName && destinationName) {
    const title = appName;
    const description = `Vuelos desde ${originName} a ${destinationName}. Compará precios, aerolíneas y horarios en ${appName}.`;
    return { title, description };
  }

  return {
    title: appName,
    description: `Encontrá los mejores vuelos en ${appName}. Compará precios, aerolíneas y horarios. Reservá online.`,
  };
};

const FlightListPage = () => {
  const [isMobile, setIsMobile] = useState(false);
  const { onSearchCount } = useGlobal();
  const { search } = useLocation();
  const appName = useAppName();

  const hasValidSearchParams = useMemo(() => {
    if (!search) return false;

    const searchParams = new URLSearchParams(search);
    const hasOrigin = searchParams.get('originId') && searchParams.get('originName');
    const hasDestination = searchParams.get('destinationId') && searchParams.get('destinationName');
    const hasDates = searchParams.get('departureDate');

    return hasOrigin && hasDestination && hasDates;
  }, [search]);

  const seoData = useMemo(() => buildSeoFromParams(search, appName), [search, appName]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isClient ? window.innerWidth <= FLIGHT_RESULTS_FILTERS_MOBILE_BREAKPOINT : false);
    };
    handleResize();
    isClient && window.addEventListener('resize', handleResize);
    return () => isClient && window.removeEventListener('resize', handleResize);
  }, []);

  const shouldShowFilters = onSearchCount > 0 || hasValidSearchParams;

  return (
    <>
      <Seo {...seoData} />
      <FlightProvider>
        <FlightsFiltersProvider>
          <SearchSelector isOnTargetPath />
          {shouldShowFilters && (
            <Stack
              direction='row'
              display='flex'
              sx={{
                padding: '4vw 2vw 0 2vw',
                marginBottom: '3rem',
              }}
            >
              {!isMobile ? (
                <FlightsAsideFilters />
              ) : (
                shouldShowFilters && (
                  <MobileFlightsFilter>
                    <FlightsAsideFilters isMobile />
                  </MobileFlightsFilter>
                )
              )}
              {shouldShowFilters && <FlightsListView />}
            </Stack>
          )}
        </FlightsFiltersProvider>
      </FlightProvider>
    </>
  );
};

export default FlightListPage;
